import { statusTone, StatusTone } from '../../theme';
import { getStatusMeta } from '../../utils/statusMeta';

/**
 * Labels and colours for editor tasks, shared by the task list, the timeline
 * and the feedback panel so a task reads the same everywhere. Status words
 * and tones come from the app-wide status map (utils/statusMeta).
 */

export interface WorkspaceTask {
  id: string;
  title: string;
  description?: string | null;
  type?: string | null;
  status?: string | null;
  priority?: string | null;
  timeline?: { start?: number | null; end?: number | null; frameStart?: number | null; frameEnd?: number | null } | null;
  assetRefs?: string[] | null;
  assetInstructions?: string[] | null;
  aiInstructions?: string | null;
  estimatedMinutes?: number | null;
  actualMinutes?: number | null;
  dependencies?: string[] | null;
  tags?: string[] | null;
  editorNotes?: string | null;
  reviewComments?: string | null;
  generatedFromAnnotations?: string[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  completedAt?: string | null;
}

export interface TaskStatusDisplay {
  /** "To do", "In progress", "Done"… (statusMeta's task labels). */
  label: string;
  tone: StatusTone;
  /** The tone's main shade: timeline bars, dots and the card's left rule. */
  color: string;
  /** Board order: work in progress first, finished last. */
  order: number;
}

const TASK_ORDER: Record<string, number> = {
  IN_PROGRESS: 0,
  REVIEW_NEEDED: 1,
  BLOCKED: 2,
  PENDING: 3,
  COMPLETED: 4,
};

/** A task with no status yet is a to-do. */
export const taskStatusKey = (status?: string | null): string => String(status || '').trim().toUpperCase() || 'PENDING';

export const taskStatusMeta = (status?: string | null): TaskStatusDisplay => {
  const key = taskStatusKey(status);
  const meta = getStatusMeta('task', key);
  return {
    label: meta.label,
    tone: meta.tone,
    color: statusTone[meta.tone].main,
    order: TASK_ORDER[key] ?? TASK_ORDER.PENDING,
  };
};

/** The statuses an editor can move a task between (the workspace's status menu). */
export const TASK_STATUS_CHOICES = ['PENDING', 'IN_PROGRESS', 'COMPLETED'] as const;
export type TaskStatusChoice = typeof TASK_STATUS_CHOICES[number];

const TASK_TYPE_LABELS: Record<string, string> = {
  ANNOTATION: 'Feedback',
  ASSET_INTEGRATION: 'Asset integration',
  EFFECT_APPLICATION: 'Effects',
  TRANSITION: 'Transition',
  COLOR_CORRECTION: 'Colour correction',
  AUDIO_SYNC: 'Audio',
  MISC: 'General',
};

export const taskTypeLabel = (type?: string | null) =>
  TASK_TYPE_LABELS[String(type || '').toUpperCase()] ?? (type ? String(type) : 'General');

export const taskStart = (task: WorkspaceTask): number | null => {
  const start = Number(task.timeline?.start);
  return Number.isFinite(start) && start >= 0 ? start : null;
};

export const taskEnd = (task: WorkspaceTask): number | null => {
  const start = taskStart(task);
  const end = Number(task.timeline?.end);
  return start !== null && Number.isFinite(end) && end > start ? end : null;
};

/** Task progress the same way the backend summarises it. */
export const summarizeTasks = (tasks: WorkspaceTask[]) => {
  const total = tasks.length;
  const completed = tasks.filter((t) => String(t.status).toUpperCase() === 'COMPLETED').length;
  const inProgress = tasks.filter((t) => String(t.status).toUpperCase() === 'IN_PROGRESS').length;
  const pending = total - completed - inProgress;
  const estimated = tasks.reduce((sum, t) => sum + (Number(t.estimatedMinutes) || 0), 0);
  const actual = tasks.reduce((sum, t) => sum + (Number(t.actualMinutes) || 0), 0);
  const remaining = tasks
    .filter((t) => String(t.status).toUpperCase() !== 'COMPLETED')
    .reduce((sum, t) => sum + (Number(t.estimatedMinutes) || 0), 0);
  return {
    total,
    completed,
    inProgress,
    pending,
    estimatedMinutes: estimated,
    actualMinutes: actual,
    remainingMinutes: remaining,
    percent: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
};

/** "1h 25m", "40m". */
export const formatMinutes = (minutes: number) => {
  const safe = Math.max(0, Math.round(minutes));
  if (safe < 60) return `${safe}m`;
  const hours = Math.floor(safe / 60);
  const rest = safe % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};
